import { MotifId } from '../challenge.model';

/**
 * Each motif is centered on (0, 0) and sized so the *visual radius* is approximately `size` units.
 * Returns a snippet of SVG markup. The caller wraps it in a positioning `<g transform=...>`.
 *
 * `filled = true`  → solid shape painted in `color` (the habit is completed today).
 * `filled = false` → outline-only "shell" in `color` (habit slot, not yet done today).
 */
export interface MotifDef {
  id: MotifId;
  label: string;
  render(size: number, color: string, filled: boolean): string;
}

function paint(filled: boolean, color: string, strokeWidth = 1.2): string {
  return filled
    ? `fill="${color}"`
    : `fill="none" stroke="${color}" stroke-width="${strokeWidth}" opacity="0.85"`;
}

export const MOTIFS: Record<MotifId, MotifDef> = {
  'slim-petal': {
    id: 'slim-petal',
    label: 'Slim petal',
    render: (s, c, f) => `<ellipse cx="0" cy="0" rx="${s * 0.35}" ry="${s * 1.1}" ${paint(f, c, Math.max(0.8, s * 0.12))}/>`,
  },
  'round-petal': {
    id: 'round-petal',
    label: 'Round petal',
    render: (s, c, f) => `<ellipse cx="0" cy="0" rx="${s * 0.75}" ry="${s * 1.1}" ${paint(f, c, Math.max(0.8, s * 0.12))}/>`,
  },
  'leaf': {
    id: 'leaf',
    label: 'Leaf',
    render: (s, c, f) => {
      const h = s * 1.1, w = s * 0.55;
      return `<path d="M0,-${h} Q${w},0 0,${h} Q-${w},0 0,-${h} Z" ${paint(f, c, Math.max(0.8, s * 0.12))}/>`;
    },
  },
  'triangle': {
    id: 'triangle',
    label: 'Triangle',
    render: (s, c, f) => {
      const h = (s * 1.1).toFixed(2), w = (s * 0.85).toFixed(2), hm = (s * 1.1 * 0.5).toFixed(2);
      return `<path d="M0,-${h} L${w},${hm} L-${w},${hm} Z" ${paint(f, c, Math.max(0.8, s * 0.14))}/>`;
    },
  },
  'spike': {
    id: 'spike',
    label: 'Spike',
    render: (s, c, f) => {
      const h = s * 1.3, w = s * 0.4;
      return `<path d="M0,-${h} L${w},${h * 0.7} L-${w},${h * 0.7} Z" ${paint(f, c, Math.max(0.8, s * 0.14))}/>`;
    },
  },
  'diamond': {
    id: 'diamond',
    label: 'Diamond',
    render: (s, c, f) => {
      const r = s;
      return `<path d="M0,-${r} L${r},0 L0,${r} L-${r},0 Z" ${paint(f, c, Math.max(0.8, s * 0.14))}/>`;
    },
  },
  'dot': {
    id: 'dot',
    label: 'Dot',
    render: (s, c, f) => `<circle cx="0" cy="0" r="${s * 0.85}" ${paint(f, c, Math.max(0.8, s * 0.16))}/>`,
  },
  'star-5': {
    id: 'star-5',
    label: '5-pt star',
    render: (s, c, f) => {
      const o = s * 1.1, i = s * 0.45;
      const pts: string[] = [];
      for (let k = 0; k < 10; k++) {
        const r = k % 2 === 0 ? o : i;
        const a = (Math.PI / 5) * k - Math.PI / 2;
        pts.push(`${(r * Math.cos(a)).toFixed(2)},${(r * Math.sin(a)).toFixed(2)}`);
      }
      return `<polygon points="${pts.join(' ')}" ${paint(f, c, Math.max(0.8, s * 0.12))}/>`;
    },
  },
  'burst': {
    id: 'burst',
    label: 'Burst',
    render: (s, c, f) => {
      const o = s * 1.15, i = s * 0.2;
      return `<path d="M0,-${o} L${i},-${i} L${o},0 L${i},${i} L0,${o} L-${i},${i} L-${o},0 L-${i},-${i} Z" ${paint(f, c, Math.max(0.8, s * 0.12))}/>`;
    },
  },
  'hexagon': {
    id: 'hexagon',
    label: 'Hexagon',
    render: (s, c, f) => {
      const r = s;
      const pts: string[] = [];
      for (let k = 0; k < 6; k++) {
        const a = (Math.PI / 3) * k - Math.PI / 2;
        pts.push(`${(r * Math.cos(a)).toFixed(2)},${(r * Math.sin(a)).toFixed(2)}`);
      }
      return `<polygon points="${pts.join(' ')}" ${paint(f, c, Math.max(0.8, s * 0.14))}/>`;
    },
  },
  'crescent': {
    id: 'crescent',
    label: 'Crescent',
    render: (s, c, f) => {
      const r = s;
      return `<path d="M${r * 0.5},0 A${r},${r} 0 1,1 -${r * 0.2},-${r * 0.5} A${r * 0.7},${r * 0.7} 0 1,0 ${r * 0.5},0 Z" ${paint(f, c, Math.max(0.8, s * 0.12))}/>`;
    },
  },
  'teardrop': {
    id: 'teardrop',
    label: 'Teardrop',
    render: (s, c, f) => {
      const r = s;
      return `<path d="M0,-${r * 1.1} Q${r * 0.7},${r * 0.2} ${r * 0.65},${r * 0.55} A${r * 0.65},${r * 0.65} 0 1,1 -${r * 0.65},${r * 0.55} Q-${r * 0.7},${r * 0.2} 0,-${r * 1.1} Z" ${paint(f, c, Math.max(0.8, s * 0.12))}/>`;
    },
  },
  'trefoil': {
    id: 'trefoil',
    label: 'Trefoil',
    render: (s, c, f) => {
      const armR = s * 0.9, armW = s * 0.4;
      const arms: string[] = [];
      for (let k = 0; k < 3; k++) {
        const rot = k * 120;
        arms.push(`<ellipse cx="0" cy="-${armR * 0.55}" rx="${armW}" ry="${armR * 0.7}" ${paint(f, c, Math.max(0.8, s * 0.1))} transform="rotate(${rot})"/>`);
      }
      return `<g>${arms.join('')}</g>`;
    },
  },
  'rosette': {
    id: 'rosette',
    label: 'Rosette',
    render: (s, c, f) => {
      const armR = s * 0.9, armW = s * 0.32;
      const arms: string[] = [];
      for (let k = 0; k < 5; k++) {
        const rot = k * 72;
        arms.push(`<ellipse cx="0" cy="-${armR * 0.55}" rx="${armW}" ry="${armR * 0.7}" ${paint(f, c, Math.max(0.8, s * 0.1))} transform="rotate(${rot})"/>`);
      }
      return `<g>${arms.join('')}</g>`;
    },
  },
  'plus': {
    id: 'plus',
    label: 'Plus',
    render: (s, c, f) => {
      const arm = s, w = s * 0.35;
      return `<path d="M-${w},-${arm} h${w * 2} v${arm - w} h${arm - w} v${w * 2} h-${arm - w} v${arm - w} h-${w * 2} v-${arm - w} h-${arm - w} v-${w * 2} h${arm - w} z" ${paint(f, c, Math.max(0.8, s * 0.14))}/>`;
    },
  },
  'curl': {
    id: 'curl',
    label: 'Curl',
    render: (s, c, f) => {
      // Curl is always a stroke. Filled = thicker bright stroke; shell = thin dashed.
      const r = s;
      const sw = f ? r * 0.3 : Math.max(0.8, r * 0.16);
      const dash = f ? '' : ` stroke-dasharray="${(r * 0.4).toFixed(2)},${(r * 0.3).toFixed(2)}"`;
      const opacity = f ? '1' : '0.85';
      return `<path d="M0,-${r} C${r * 0.7},-${r * 0.4} ${r * 0.7},${r * 0.4} 0,${r} C-${r * 0.7},${r * 0.4} -${r * 0.7},-${r * 0.4} 0,-${r} Z" fill="none" stroke="${c}" stroke-width="${sw}" stroke-linecap="round" opacity="${opacity}"${dash}/>`;
    },
  },
};

export function motifPathById(id: MotifId, size: number, color: string, filled = true): string {
  return MOTIFS[id].render(size, color, filled);
}
